import { Button } from '../../../shared/formElements/button';
import * as Yup from 'yup';
import {
  ButtonGroupContainer,
  Form,
  FormContainer,
  FormSectionContainer,
  Line,
  LoadingSpinner,
  SectionContainer,
} from '../../../shared/Layout.styles';
import { Markdown } from '../../../shared/markdown';
import { kycVerificationValidationConstants } from '../../../utils/constants/kyc';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useContextDispatch,
  useContextState,
} from '../../context/context';
import { KycVerificationValidationInputs } from './sections';
import { useEffect, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { OTPModal } from '../../modals';
import { useHistory } from 'react-router';

const {
  cardID,
  day,
  month,
  year,
  suburb,
  postcode,
  state,
  country,
  streetName,
  flatNumber,
  streetNumber,
  streetType,
  consentGranted,
} = kycVerificationValidationConstants.validations;
type ModalState = {phone: string | null, open: false, token: string | null} | {phone: string, token: string | null, open: true, resolve: (result: boolean) => void}

export const KycVerification = () => {
  const dispatch = useContextDispatch();
  const { loading, cardholderAuth, otpToken } = useContextState();
  const history = useHistory();

  const [serverErrors, setServerErrors] = useState('');
  const [serverSuccess, setServerSuccess] = useState('');
  const [modalState, setModalState] = useState<ModalState>({phone: null, open: false, token: null})

  const {
    heading,
    subHeading,
    bodyText
  } = kycVerificationValidationConstants;

  const handleClose = () => {
    if (modalState.open) {
      modalState.resolve(true)
      setModalState({phone: '',open: false, token:null})
    }
  }


  const authenticate = async (phone: string) => {
    try{
      const data = {
        mobile_number: phone
      };
      await axios.post('/v1/requestotp', data, {
        headers: {
          'X-API-Key': process.env.REACT_APP_VAULT_API_KEY || ''
        },
      });
      await new Promise((resolve) => {
        setModalState({open: true, token: null, phone: phone, resolve})
      } )
    }
    catch (err:any) {
      console.log('err', err);
      if (err.response.status === 400 || err.response.status === 401) {
        setServerErrors(err.response.data.message);
      }
    }
  };

  const authHeader = (token: string) => {
    // return auth header with jwt if a OTP was required for activation
      return { Authorization: `Bearer ${token}` };
  }

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
    shouldFocusError: false,
    defaultValues: {
      cardID: '',
      phone: '',
      day: '',
      month: '',
      year: '',
      streetName: '',
      streetType: '',
      flatNumber: '',
      streetNumber: '',
      suburb: '',
      postcode: '',
      state: '',
      country: '',
      fullAddress: '',
      consentGranted: false,
    },
  });

  const onSubmit = async (formData: any) => {
    dispatch('START_LOADING');

    if (!otpToken){
      await authenticate(formData.phoneNumber)
      dispatch('STOP_LOADING');
      return;
    }
    try {
      setServerSuccess('');
      setServerErrors('');
      const data = {
        eaid: formData.cardID,
        dob: [ // avoid timezone issues, this dob is directly added into green id submission and mismatches requires admin approval
          formData.year,
          String(formData.month).padStart(2, '0'),
          String(formData.day).padStart(2, '0'),
        ].join('-'),
        address: {
          flat_number: formData.flatNumber,
          street_number: formData.streetNumber,
          street_type: formData.streetType,
          street_name: formData.streetName,
          postcode: formData.postcode,
          suburb: formData.suburb,
          state: formData.state,
          country: formData.country,
        },
        consent_granted: formData.consentGranted === true,
      };
      let config: AxiosRequestConfig = {
        headers: {
          'X-API-Key': process.env.REACT_APP_VAULT_API_KEY || ''
        },
      };

      if (otpToken) {
        config = {
          ...config,
          headers: {
            ...config.headers,
            ...authHeader(otpToken),
          },
        };
      }
      const response = await axios.post(`/v1/kycverify`, data, config);
      if (response.data.kyc_status === 'compliant'){
        // card was verified and doesnt need any further checks
        setServerSuccess(
          'Thank you for completing your KYC verification. Green ID has successfully verified your identity, and your card is now ready to use.'
        );
        dispatch('CLEAR_AUTH')
      } else if (response.data.verification_token) {
        // a green id journey was started and needs to be completed
        dispatch('SET_KYC', {
          verificationId: response.data.verification_id,
          verificationToken: response.data.verification_token,
          verificationStatus: null,
          vmpKycStatus: response.data.kyc_status,
          cardId: response.data.card_id
        });
        dispatch('SET_AUTH', response.data.token)
        history.push('/green-id');
      } else {
       setServerErrors('There was an issue with your submission. Please try again.');
      }
      dispatch('STOP_LOADING');
    } catch (err: any) {
      if (err && err?.response?.status === 400 && err?.response?.data?.message) {
        setServerErrors(err?.response?.data?.message);
      } else if (err && err.message) {
        setServerErrors(err.message);
      } else {
        setServerErrors("Unknown Error");
      }
      dispatch('STOP_LOADING');
    }
  };

  useEffect(() => {
    if (otpToken) {
      methods.handleSubmit(onSubmit)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpToken]);

  return (
    <>
        <Line />
      { modalState.open && <OTPModal {...modalState} handleClose={handleClose} /> }

      <FormProvider {...methods}>
          <Form onSubmit={methods.handleSubmit(onSubmit)}>
            <SectionContainer>
              <Markdown children={heading} />
              <Markdown children={subHeading} />
              <Markdown children={bodyText} className="subText"/>

              <FormContainer>
                <FormSectionContainer className="kycValidateSection">
                 {serverSuccess ? (
                    <Markdown
                      children={serverSuccess}
                      className="successText"
                    />
                  ) : <KycVerificationValidationInputs />}
                  {serverErrors && (
                    <Markdown
                      children={serverErrors}
                      className="validationText"
                    />
                  )}

                  {serverSuccess ? null :
                    <ButtonGroupContainer>
                      <Button disabled={ loading } >
                        {loading ? (
                          <LoadingSpinner show />
                        ) : (
                          cardholderAuth ? "Submit" : "Request OTP"
                        )}
                      </Button>
                    </ButtonGroupContainer>
                  }
                </FormSectionContainer>
              </FormContainer>
            </SectionContainer>
          </Form>
        </FormProvider>
    </>
  );
};
const validationSchema = Yup.object().shape({
    cardID: Yup.string()
    .required(cardID.required)
    .matches(/[\dA-Za-z]+/, {
      message: cardID.format,
      excludeEmptyString: true,
    }),
    day: Yup.string()
    .required(day.required)
    .max(2, "Please enter a valid day.")
    .test("day", "Please enter a valid day.", (value) => parseInt(value!) <= 31 && parseInt(value!) > 0)
    .test({
      name: "dayOfFeb",
      message: "Please enter a valid day in February.",
      test: (value, ctx) => {
        const month = ctx.parent.month != null ? parseInt(ctx.parent.month) : undefined;
        const year = ctx.parent.year != null ? parseInt(ctx.parent.year) : undefined;
        // Handle the leap year!
        if (year && (year % 4) === 0) {
          if (month === 2) {
            return parseInt(value!) <= 29;
          }
        }
        if (month === 2) {
          return parseInt(value!) <= 28;
        }
        return true;
      }
    })
    .test({
      name: "dayOf30",
      message: "Please enter a valid day.",
      test: (value, ctx) => {
        const month = ctx.parent.month != null ? parseInt(ctx.parent.month) : undefined;
        if (month === 4 ||
          month === 6 ||
          month === 9 ||
          month === 11) {
          return parseInt(value!) <= 30;
        }
        return true;
      }
    })
    .matches(/[0-9]+/, {
      message: day.format,
      excludeEmptyString: true,
    }),
    month: Yup.string()
    .required(month.required)
    .max(2, "Please enter a valid month.")
    .test("month", "Please enter a valid month.", (value) => parseInt(value!) <= 12 && parseInt(value!) > 0)
    .matches(/[0-9]+/, {
      message: month.format,
      excludeEmptyString: true,
    }),
    year: Yup.string()
    .required(year.required)
    .max(4, "Please enter a valid year.")
    .test("year", "Please enter a valid year.", (value) => parseInt(value!) >= 1800 && parseInt(value!) < (new Date().getFullYear() + 1))
    .matches(/[0-9]+/, {
      message: year.format,
      excludeEmptyString: true,
    }),
    suburb: Yup.string()
    .required(suburb.required)
    .matches(/^([A-Za-z\s\-'".])*$/, {
      message: suburb.format,
      excludeEmptyString: true,
    }),
  streetName: Yup.string()
    .required(streetName.required)
    .matches(/^([A-Za-z0-9\s\-'".])*$/, {
      message: streetName.format,
      excludeEmptyString: true,
    }),
  flatNumber: Yup.string()
    .matches(/^([0-9A-Za-z \/'".-])*$/, {
      message: flatNumber.format,
      excludeEmptyString: true,
    }),
  streetNumber: Yup.string()
    .required(streetNumber.required)
    .matches(/^([0-9A-Za-z \/'".-])*$/, {
      message: streetNumber.format,
      excludeEmptyString: true,
    }),
  streetType: Yup.string()
    .required(streetType.required)
    .matches(/^([A-Za-z\s\-'".])*$/, {
      message: streetType.format,
      excludeEmptyString: true,
    }),
    postcode: Yup.string()
    .required(postcode.required)
    .matches(/^([A-Za-z0-9\s\-'".])*$/, {
      message: postcode.format,
      excludeEmptyString: true,
    }),
    state: Yup.string()
    .required(state.required)
    .matches(/^([A-Za-z\s\-'".])*$/, {
      message: state.format,
      excludeEmptyString: true,
    }),
    country: Yup.string()
    .required(country.required)
    .matches(/^([A-Za-z\s\-'".])*$/, {
      message: country.format,
      excludeEmptyString: true,
    }),
  consentGranted: Yup.bool().oneOf([true], consentGranted.required),
});
